import { queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'

export type BenchmarkRun = Database['public']['Tables']['benchmark_runs']['Row']

export type BenchmarkRunStatus = BenchmarkRun['status']

export interface BenchmarkSession {
  sessionId: string
  sessionName: string | null
  createdAt: string
  createdByName: string | null
  total: number
  completed: number
  failed: number
  incompatible: number
  processing: number
}

export const benchmarkRun = {
  findSessionList: () =>
    queryOptions({
      queryKey: ['benchmark-runs', 'sessions'],
      queryFn: async (): Promise<BenchmarkSession[]> => {
        const { data, error } = await supabase
          .from('benchmark_runs')
          .select(
            'session_id, session_name, created_at, status, profiles!inner(full_name)'
          )
          .order('created_at', { ascending: false })

        if (error) {
          throw new Error(
            `Failed to fetch benchmark sessions: ${error.message}`,
            { cause: error }
          )
        }

        // Group by session_id
        const sessionMap = new Map<string, BenchmarkSession>()
        for (const row of data ?? []) {
          const profileData = row.profiles as unknown as {
            full_name: string | null
          } | null
          let session = sessionMap.get(row.session_id)
          if (!session) {
            session = {
              sessionId: row.session_id,
              sessionName: row.session_name,
              createdAt: row.created_at,
              createdByName: profileData?.full_name ?? null,
              total: 0,
              completed: 0,
              failed: 0,
              incompatible: 0,
              processing: 0,
            }
            sessionMap.set(row.session_id, session)
          }
          session.total++
          if (row.status === 'completed') {
            session.completed++
          } else if (row.status === 'failed') {
            session.failed++
          } else if (row.status === 'incompatible') {
            session.incompatible++
          } else if (row.status === 'processing') {
            session.processing++
          }
        }

        // Sort by most recent first
        return Array.from(sessionMap.values()).sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )
      },
      staleTime: 30 * 1000,
    }),

  findManyBySession: (sessionId: string) =>
    queryOptions({
      queryKey: ['benchmark-runs', 'session', sessionId],
      queryFn: async (): Promise<BenchmarkRun[]> => {
        if (!sessionId || sessionId.trim() === '') {
          throw new Error(
            'Invalid sessionId: cannot fetch benchmark runs without valid session ID'
          )
        }

        const { data, error } = await supabase
          .from('benchmark_runs')
          .select('*')
          .eq('session_id', sessionId)
          .order('created_at', { ascending: true })

        if (error) {
          throw new Error(`Failed to fetch benchmark runs: ${error.message}`, {
            cause: error,
          })
        }

        return data ?? []
      },
      staleTime: 10 * 1000,
    }),
}

export interface ReplicateModelInfo {
  owner: string
  name: string
  description: string | null
  cover_image_url: string | null
  run_count: number
  url: string
}

export const replicateModel = {
  search: (query?: string) =>
    queryOptions({
      queryKey: ['replicate-models', query ?? ''],
      queryFn: async (): Promise<ReplicateModelInfo[]> => {
        const params = new URLSearchParams()
        if (query && query.trim().length > 0) {
          params.set('query', query.trim())
        }
        const qs = params.toString()
        const url = qs ? `benchmark-models?${qs}` : 'benchmark-models'

        const { data, error } = await supabase.functions.invoke(url, {
          method: 'GET',
        })

        if (error) {
          // Check if the response body contains a specific error code
          const errorData = data as
            { error?: string; message?: string } | undefined
          if (errorData?.error === 'replicate_not_configured') {
            throw new Error(errorData.message ?? 'replicate_not_configured')
          }
          throw new Error('Failed to search Replicate models', {
            cause: error,
          })
        }

        return (data as { models: ReplicateModelInfo[] }).models
      },
      staleTime: 5 * 60 * 1000,
      retry: (failureCount, error) => {
        // Don't retry configuration errors
        if (
          error.message.includes('replicate_not_configured') ||
          error.message.includes('REPLICATE_API_TOKEN')
        ) {
          return false
        }
        return failureCount < 3
      },
    }),
}

export interface CreateBenchmarkRunRequest {
  session_id: string
  session_name: string
  user_id: string
  runs: Array<{
    id?: string
    replicate_model: string
    feature_type: 'stp' | 'vto' | 'model' | 'scene' | 'custom'
    test_case_id: string
    test_case_name: string
    prompt: string
    reference_images?: string[]
    custom_inputs?: Record<string, unknown>
  }>
}

export async function createBenchmarkRun(
  request: CreateBenchmarkRunRequest
): Promise<{ session_id: string; run_ids: string[] }> {
  const runRows = request.runs.map((run) => ({
    id: run.id ?? crypto.randomUUID(),
    session_id: request.session_id,
    session_name: request.session_name,
    feature_type: run.feature_type,
    test_case_id: run.test_case_id,
    replicate_model: run.replicate_model,
    status: 'pending' as const,
    input_params: JSON.parse(
      JSON.stringify({
        test_case_name: run.test_case_name,
        prompt: run.prompt,
        reference_images: run.reference_images,
        custom_inputs: run.custom_inputs,
      })
    ),
    created_by: request.user_id,
  }))

  const { error } = await supabase.from('benchmark_runs').insert(runRows)

  if (error) {
    throw new Error('Failed to create benchmark runs', { cause: error })
  }

  const runIds = runRows.map((r) => r.id)

  // Fan out: kick off each run in its own edge function isolate.
  // Fire-and-forget — each EF updates the DB row directly,
  // and the realtime subscription streams results to the UI.
  // Stagger in batches to stay under Replicate's burst rate limit
  // (burst of 5 requests, 60/min with <$10 credit).
  // Each replicate.run() = create + poll, so 2 concurrent runs ≈ 4-5 API calls.
  const BATCH_SIZE = 2
  const BATCH_DELAY_MS = 3000

  void (async () => {
    for (let i = 0; i < runIds.length; i += BATCH_SIZE) {
      const batch = runIds.slice(i, i + BATCH_SIZE)
      for (const runId of batch) {
        supabase.functions
          .invoke('benchmark-process-run', { body: { run_id: runId } })
          .catch(() => {
            // Errors handled inside the EF (updates DB row status).
            // If invocation itself fails, stale-run detection catches it.
          })
      }
      if (i + BATCH_SIZE < runIds.length) {
        await new Promise((resolve) => setTimeout(resolve, BATCH_DELAY_MS))
      }
    }
  })()

  return { session_id: request.session_id, run_ids: runIds }
}

export async function deleteBenchmarkSession(sessionId: string): Promise<void> {
  // Delete storage files for this session
  const { data: files } = await supabase.storage
    .from('benchmarks')
    .list(`benchmarks/${sessionId}`)

  if (files && files.length > 0) {
    const paths = files.map((f) => `benchmarks/${sessionId}/${f.name}`)
    await supabase.storage.from('benchmarks').remove(paths)
  }

  // Delete all runs for this session (RLS enforces super admin)
  const { error } = await supabase
    .from('benchmark_runs')
    .delete()
    .eq('session_id', sessionId)

  if (error) {
    throw new Error('Failed to delete benchmark session', { cause: error })
  }
}
